import { useCallback, useEffect, useState } from 'react';
import {
  getCompetitionTeam, updateCompetitionTeam, updateCompetitionMember,
  removeCompetitionMember, assetUrl,
} from '../../services/api';
import { COMPETITION_STATS, MAX_STAT } from '../../utils/competition';
import { getRumaalColor } from '../../utils/colors';
import AddDancers from './AddDancers';
import StatGrid from './StatGrid';

// One team: who is on it, where they stand, and the way in to recording a
// session. The leaderboard is the point of the screen — the client's ask was
// "each student's ranking within their own team" — so it leads, and roster
// admin sits inside the rows rather than on a separate page.

const statLabel = (key) => COMPETITION_STATS.find((stat) => stat.key === key)?.label || key;

export default function TeamView({ teamId, onBack }) {
  const [payload, setPayload] = useState(null);
  const [tab, setTab] = useState('team');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const [reloadAt, setReloadAt] = useState(0);
  const load = useCallback(() => setReloadAt((n) => n + 1), []);

  // The fetch lives in the effect rather than in a callback the effect calls,
  // so a team switched while a response is in flight discards the stale one
  // instead of painting the previous team's roster over the new one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getCompetitionTeam(teamId);
        if (!cancelled) setPayload(data);
      } catch (loadError) {
        if (!cancelled) setError(loadError.response?.data?.error || 'Could not load that team');
      }
    })();
    return () => { cancelled = true; };
  }, [teamId, reloadAt]);

  const saveMember = async (studentKey, changes) => {
    // Optimistic: the coach is typing a role, and a round trip per keystroke
    // would make the field feel broken.
    setPayload((p) => ({
      ...p,
      members: p.members.map((m) => (m.studentKey === studentKey ? { ...m, ...changes } : m)),
    }));
    try {
      await updateCompetitionMember(teamId, studentKey, changes);
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Could not save that');
      load();
    }
  };

  const remove = async (member) => {
    if (!window.confirm(`Take ${member.name} off ${payload.team.name}? Their recorded stats are kept.`)) return;
    try {
      await removeCompetitionMember(teamId, member.studentKey);
      load();
    } catch (removeError) {
      setError(removeError.response?.data?.error || 'Could not remove that dancer');
    }
  };

  const toggleActive = async () => {
    try {
      await updateCompetitionTeam(teamId, { active: !payload.team.active });
      load();
    } catch (toggleError) {
      setError(toggleError.response?.data?.error || 'Could not update that team');
    }
  };

  if (error && !payload) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error}</p>
        <button onClick={onBack} className="mt-4 rounded-md bg-gray-900 px-4 py-2 text-white">Back</button>
      </div>
    );
  }
  if (!payload) return <p className="p-8 text-gray-500">Loading…</p>;

  const { team, members, sessions } = payload;
  const scored = members.filter((m) => m.score !== null);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={onBack} className="mb-2 text-sm text-purple-600 hover:text-purple-700">
            ← All teams
          </button>
          <h2 className="text-2xl font-bold text-gray-900">{team.name}</h2>
          <p className="text-sm text-gray-500">
            {team.season || 'No season set'} · {members.length} dancer{members.length === 1 ? '' : 's'}
            {sessions.length > 0 && ` · ${sessions.length} session${sessions.length === 1 ? '' : 's'}`}
            {!team.active && ' · inactive'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={toggleActive}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {team.active ? 'Mark inactive' : 'Mark active'}
          </button>
          <button
            onClick={() => setAdding(true)}
            className="rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700"
          >
            + Add dancers
          </button>
        </div>
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <div className="mb-3 flex gap-1.5">
        {[['team', 'Team'], ['record', 'Record a session']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === key ? 'bg-purple-600 text-white' : 'border border-gray-200 bg-white text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'record' ? (
        <StatGrid teamId={teamId} members={members} sessions={sessions} onSaved={load} />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            {members.map((member) => (
              <MemberRow
                key={member.studentKey}
                member={member}
                onRole={(role) => saveMember(member.studentKey, { role })}
                onRemove={() => remove(member)}
              />
            ))}

            {members.length === 0 && (
              <p className="p-8 text-center text-sm text-gray-500">
                Nobody is on this team yet — add the dancers who are competing.
              </p>
            )}
          </div>

          {members.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Ranked on the average of all six stats across every recorded session.
              {scored.length < members.length &&
                ` ${members.length - scored.length} dancer${members.length - scored.length === 1 ? ' has' : 's have'} nothing recorded yet.`}
            </p>
          )}
        </>
      )}

      {adding && (
        <AddDancers
          teamId={teamId}
          onClose={() => setAdding(false)}
          onAdded={() => { setAdding(false); load(); }}
        />
      )}
    </div>
  );
}

function MemberRow({ member, onRole, onRemove }) {
  const initials = member.name.split(' ').map((w) => w[0]).slice(0, 2).join('');

  return (
    <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0">
      <div className="w-8 shrink-0 text-center text-sm font-bold text-gray-900">
        {member.position ? `#${member.position}` : <span className="text-gray-300">—</span>}
      </div>

      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
        style={{ boxShadow: `0 0 0 2px ${getRumaalColor(member.rumaalRank)}, 0 0 0 3px rgba(0,0,0,.15)` }}
      >
        {member.photoUrl
          ? <img src={assetUrl(member.photoUrl)} alt="" className="h-full w-full object-cover" />
          : <span className="text-[11px] font-semibold text-gray-400">{initials}</span>}
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-gray-900">{member.name}</div>
        <div className="truncate text-xs text-gray-500">
          {[member.rumaalRank, member.ageDivision, member.className].filter(Boolean).join(' · ') || 'No class'}
        </div>
        <RoleField value={member.role} onCommit={onRole} />
      </div>

      <div className="hidden w-56 shrink-0 sm:block">
        {member.score === null ? (
          <span className="text-xs text-gray-400">Nothing recorded yet</span>
        ) : (
          <StatBars stats={member.stats} focusStat={member.focusStat} />
        )}
      </div>

      <div className="w-16 shrink-0 text-right">
        <div className={`text-lg font-bold ${member.score === null ? 'text-gray-300' : 'text-gray-900'}`}>
          {member.score === null ? '—' : member.score.toFixed(1)}
        </div>
        {member.sessions > 0 && (
          <div className="text-[10px] text-gray-400">
            {member.sessions} session{member.sessions === 1 ? '' : 's'}
          </div>
        )}
      </div>

      <button
        onClick={onRemove}
        title={`Remove ${member.name} from the team`}
        className="shrink-0 rounded-md px-2 py-1 text-xs text-gray-400 hover:bg-red-50 hover:text-red-600"
      >
        Remove
      </button>
    </div>
  );
}

/** The dancer's line — "Front line", "Dhol", "Captain". Saved when they leave it. */
function RoleField({ value, onCommit }) {
  const [text, setText] = useState(value || '');
  const [lastSeen, setLastSeen] = useState(value);

  if (value !== lastSeen) {
    setLastSeen(value);
    setText(value || '');
  }

  return (
    <input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { if (text !== (value || '')) onCommit(text.trim()); }}
      placeholder="+ line or role"
      aria-label="Role on the team"
      className="mt-0.5 w-40 rounded border border-transparent bg-transparent px-1 py-0.5 text-xs text-purple-700 placeholder:text-gray-300 hover:border-gray-200 focus:border-purple-400 focus:bg-white focus:outline-none"
    />
  );
}

/** Six small bars — the same reading the app gives a parent, in miniature. */
function StatBars({ stats, focusStat }) {
  return (
    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
      {COMPETITION_STATS.map((stat) => {
        const value = stats[stat.key];
        const isFocus = stat.key === focusStat;
        return (
          <div key={stat.key} title={`${statLabel(stat.key)} ${value === null ? 'not recorded' : value.toFixed(1)}`}>
            <div className="flex items-baseline justify-between">
              <span className="text-[9px] uppercase tracking-wide text-gray-400">{stat.short}</span>
              <span className={`text-[10px] font-semibold ${isFocus ? 'text-rose-600' : 'text-gray-600'}`}>
                {value === null ? '—' : value.toFixed(1)}
              </span>
            </div>
            <div className="h-1 rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full ${isFocus ? 'bg-rose-500' : 'bg-amber-400'}`}
                style={{ width: `${value === null ? 0 : (value / MAX_STAT) * 100}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
